import { MenuItem, TranslationField, LanguageTranslations } from '../types/menu';

const normalizeCategoryList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry ?? '').trim()))
          .filter(Boolean)
      )
    );
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }

  return [];
};

/**
 * Returns all categories for the requested display language.
 * - Source language uses `item.categories` (fallback: `item.category`)
 * - Other languages can use `props.translations[lang].categoryList`
 */
export const getCategoryList = (item: MenuItem, language: string): string[] => {
  const sourceLanguage = item.props?.sourceLanguage || 'jp';

  if (language === sourceLanguage) {
    const sourceCategories = normalizeCategoryList(item.categories);
    if (sourceCategories.length > 0) {
      return sourceCategories;
    }
    return normalizeCategoryList(item.category);
  }

  const translatedCategories = normalizeCategoryList(item.props?.translations?.[language]?.categoryList);
  if (translatedCategories.length > 0) {
    return translatedCategories;
  }

  if (language === 'en') {
    const enCategoryText = item.props?.translations?.en?.category?.text || item.props?.enCategory?.text;
    if (enCategoryText) {
      return normalizeCategoryList(enCategoryText);
    }
  }

  // Final fallback to source categories.
  const fallbackSourceCategories = normalizeCategoryList(item.categories);
  if (fallbackSourceCategories.length > 0) {
    return fallbackSourceCategories;
  }

  return normalizeCategoryList(item.category);
};

// Helper functions that work with the new primary structure
export const getDisplayText = (
  item: MenuItem, 
  field: 'name' | 'description' | 'category', 
  language: string
): string => {
  const sourceLanguage = item.props?.sourceLanguage || 'jp';

  // SOURCE LANGUAGE: Top-level fields are the single source of truth for the original language.
  // Prefer item.name / item.description / item.category so admin edits to "Description (Original)" etc. display correctly.
  if (language === sourceLanguage) {
    const topLevel = item[field];
    if (topLevel !== undefined && topLevel !== null && String(topLevel).trim() !== '') {
      return String(topLevel);
    }
    const fromProps = item.props?.translations?.[language]?.[field]?.text;
    return fromProps || (item[field] ?? '');
  }

  // PRIMARY: Try new translations structure first (for non-source languages)
  const newTranslation = item.props?.translations?.[language]?.[field]?.text;
  if (newTranslation) {
    return newTranslation;
  }
  
  // LEGACY: Fallback for English (prefer props.translations.en; enLocalization deprecated)
  if (language === 'en') {
    switch (field) {
      case 'name':
        return item.props?.translations?.en?.name?.text ?? item.props?.enLocalization?.name ?? item.name;
      case 'description':
        return item.props?.translations?.en?.description?.text ?? item.props?.enLocalization?.description ?? item.description;
      case 'category':
        return getCategoryList(item, language)[0] || item.props?.enCategory?.text || item.category;
    }
  }
  
  // Final fallback to raw data
  return item[field] || '';
};

export const isAIGenerated = (
  item: MenuItem, 
  field: 'name' | 'description' | 'category', 
  language: string
): boolean => {
  // PRIMARY: Check new translations structure
  const newTranslation = item.props?.translations?.[language]?.[field];
  if (newTranslation) {
    return newTranslation.ai || false;
  }
  
  // LEGACY: Fallback to old structure for English
  if (language === 'en') {
    switch (field) {
      case 'name':
      case 'description':
        return true; // enLocalization is always AI-generated
      case 'category':
        return item.props?.enCategory?.ai || true;
    }
  }
  
  return false;
};

export const isOriginalLanguage = (
  item: MenuItem, 
  field: 'name' | 'description' | 'category', 
  language: string
): boolean => {
  const newTranslation = item.props?.translations?.[language]?.[field];
  if (newTranslation) {
    return newTranslation.isOriginal || false;
  }
  
  // Old structure: Japanese is original, English is not
  return language === (item.props?.sourceLanguage || 'jp');
};

export const getAvailableLanguages = (item: MenuItem): string[] => {
  const languages = new Set<string>();
  
  // PRIMARY: Add languages from new translations structure
  if (item.props?.translations) {
    Object.keys(item.props.translations).forEach(lang => languages.add(lang));
  }
  
  // LEGACY: Add English if old structure exists (enLocalization deprecated; use translations.en)
  if (item.props?.translations?.en || item.props?.enLocalization || item.props?.enTranslation) {
    languages.add('en');
  }
  
  return Array.from(languages);
};

export const getLanguageName = (languageCode: string): string => {
  const languageNames: Record<string, string> = {
    'jp': 'Japanese',
    'en': 'English',
    'ko': 'Korean',
    'zh': 'Chinese',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'it': 'Italian',
    'pt': 'Portuguese',
    'ru': 'Russian'
  };
  
  return languageNames[languageCode] || languageCode.toUpperCase();
};

export const getSourceLanguage = (item: MenuItem): string => {
  return item.props?.sourceLanguage || 'jp';
};

// Removed direct translation helper functions - only using transcreated (friendly) translation now

export const updateTranslation = (
  item: MenuItem,
  language: string,
  field: 'name' | 'description' | 'category',
  value: string
): Partial<MenuItem> => {
  const sourceLanguage = item.props?.sourceLanguage || 'jp';
  const isOriginal = language === sourceLanguage;
  
  const newTranslation: TranslationField = {
    text: value,
    ai: !isOriginal,
    isOriginal,
    generatedAt: new Date().toISOString()
  };
  
  return {
    props: {
      ...item.props,
      translations: {
        ...item.props?.translations,
        [language]: {
          ...item.props?.translations?.[language],
          [field]: newTranslation
        } as LanguageTranslations
      }
    }
  };
};
